import { resolveBusinessType } from '@/services/onboarding-company';

function normalizeText(value) {
  return typeof value === 'string' ? value.trim() : '';
}

function createRepresentativePayload(representative = {}) {
  if (!representative.enabled) return null;

  return {
    fullName: normalizeText(representative.fullName),
    powerOfAttorneyDate: normalizeText(representative.powerOfAttorneyDate),
    powerOfAttorneyNumber: normalizeText(representative.powerOfAttorneyNumber),
  };
}

function createCompanyPayload(company, representative) {
  if (!company) return null;

  return {
    id: normalizeText(company.id),
    inn: normalizeText(company.inn),
    kpp: normalizeText(company.kpp),
    ogrn: normalizeText(company.ogrn),
    name: normalizeText(company.name),
    shortName: normalizeText(company.shortName),
    address: normalizeText(company.address),
    registryKind: normalizeText(company.registryKind),
    businessType: resolveBusinessType(company),
    // Backend ждёт date | null: пустая строка не проходит валидацию (422).
    sourceUpdatedOn: normalizeText(company.sourceUpdatedOn) || null,
    management: {
      name: normalizeText(company.management?.name),
      post: normalizeText(company.management?.post),
    },
    representative: createRepresentativePayload(representative),
  };
}

function createPointPayload(point) {
  return {
    address: normalizeText(point.address),
    name: normalizeText(point.name),
    posSystem: normalizeText(point.posSystem),
    phone: normalizeText(point.phone),
    contactName: normalizeText(point.contactName),
    isOpen: Boolean(point.isOpen),
  };
}

function createReferralPayload(referral) {
  const partnerId = normalizeText(referral?.partnerId);
  if (!partnerId) return null;

  return {
    partnerId,
    region: normalizeText(referral?.region).toLowerCase() || 'ru',
  };
}

export function createOnboardingPayload(draft = {}, referral = {}) {
  const account = draft.account ?? {};
  const bank = draft.bank ?? {};
  const tariff = draft.tariff ?? {};
  const documents = draft.documents ?? {};

  return {
    account: {
      email: normalizeText(account.email),
      phone: normalizeText(account.phone),
      personalDataAccepted: Boolean(account.personalDataAccepted),
    },
    referral: createReferralPayload(referral),
    company: createCompanyPayload(draft.company, draft.representative),
    bank: {
      bankId: normalizeText(bank.bankId),
      accountNumber: normalizeText(bank.accountNumber),
      bik: normalizeText(bank.bik),
      bankName: normalizeText(bank.bankName),
      correspondentAccount: normalizeText(bank.correspondentAccount),
    },
    points: Array.isArray(draft.points)
      ? draft.points.map(createPointPayload)
      : [],
    tariff: {
      tariffId: normalizeText(tariff.tariffId),
      tipsEnabled: Boolean(tariff.tipsEnabled),
      paymentModuleEnabled: Boolean(tariff.paymentModuleEnabled),
      waiterSeats: Number(tariff.waiterSeats) || 0,
      learningSeats: Number(tariff.learningSeats) || 0,
      paymentPeriod: normalizeText(tariff.paymentPeriod),
    },
    documents: {
      edoId: normalizeText(documents.edoId),
      email: normalizeText(documents.email),
    },
  };
}

// В API регистрации уходит только allowlist полей: ни пароль, ни код из письма, ни
// служебные поля формы не должны попасть в запрос случайно.
export function createRegistrationStartPayload(source = {}) {
  return {
    region: normalizeText(source.region),
    email: normalizeText(source.email),
    password: typeof source.password === 'string' ? source.password : '',
    phone: normalizeText(source.phone),
    personalDataAccepted: Boolean(source.personalDataAccepted),
    emailVerificationToken: normalizeText(source.emailVerificationToken),
  };
}

export function createRegistrationResumePayload(source = {}) {
  const payload = {
    email: normalizeText(source.email),
    password: typeof source.password === 'string' ? source.password : '',
  };
  const region = normalizeText(source.region).toLowerCase();

  return region ? { ...payload, region } : payload;
}

function sanitizeText(value, fallback = '') {
  return typeof value === 'string' ? value : fallback;
}

function sanitizeNumber(value, fallback = 0) {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
}

function sanitizeCompany(company) {
  if (!company || typeof company !== 'object') return null;

  const textKeys = [
    'id', 'inn', 'kpp', 'ogrn', 'name', 'shortName', 'address', 'region',
    'registryKind', 'businessType', 'registeredOn', 'terminatedOn', 'sourceUpdatedOn',
  ];
  const result = {};
  textKeys.forEach((key) => {
    if (company[key] !== undefined && company[key] !== null) result[key] = sanitizeText(company[key]);
  });
  result.management = {
    name: sanitizeText(company.management?.name),
    post: sanitizeText(company.management?.post),
  };

  return result;
}

// Единственный источник правды о том, что можно хранить в черновике onboarding
// (sessionStorage) и отправлять в `PATCH /applications/current`. Всё, чего нет в списке
// (пароль, код подтверждения, токены и любые чужие поля из восстановленного черновика),
// отбрасывается.
export function sanitizeOnboardingDraft(source = {}) {
  const draft = source && typeof source === 'object' ? source : {};
  const tariff = draft.tariff ?? {};

  return {
    account: {
      email: sanitizeText(draft.account?.email),
      phone: sanitizeText(draft.account?.phone),
      personalDataAccepted: Boolean(draft.account?.personalDataAccepted),
    },
    companyQuery: sanitizeText(draft.companyQuery),
    company: sanitizeCompany(draft.company),
    representative: {
      enabled: Boolean(draft.representative?.enabled),
      fullName: sanitizeText(draft.representative?.fullName),
      powerOfAttorneyDate: sanitizeText(draft.representative?.powerOfAttorneyDate),
      powerOfAttorneyNumber: sanitizeText(draft.representative?.powerOfAttorneyNumber),
    },
    bank: {
      bankId: sanitizeText(draft.bank?.bankId),
      accountNumber: sanitizeText(draft.bank?.accountNumber),
      bik: sanitizeText(draft.bank?.bik),
      bankName: sanitizeText(draft.bank?.bankName),
      correspondentAccount: sanitizeText(draft.bank?.correspondentAccount),
    },
    points: Array.isArray(draft.points)
      ? draft.points.map((point, index) => ({
        id: sanitizeText(point?.id, `point-${index + 1}`),
        address: sanitizeText(point?.address),
        name: sanitizeText(point?.name),
        posSystem: sanitizeText(point?.posSystem),
        phone: sanitizeText(point?.phone),
        contactName: sanitizeText(point?.contactName),
        isOpen: point?.isOpen === undefined ? true : Boolean(point.isOpen),
      }))
      : [],
    tariff: {
      planId: sanitizeText(tariff.planId, 'start'),
      tariffId: sanitizeText(tariff.tariffId),
      tipsEnabled: tariff.tipsEnabled === undefined ? true : Boolean(tariff.tipsEnabled),
      paymentModuleEnabled: Boolean(tariff.paymentModuleEnabled),
      waiterSeats: sanitizeNumber(tariff.waiterSeats, 5),
      learningSeats: sanitizeNumber(tariff.learningSeats, 25),
      paymentPeriod: tariff.paymentPeriod === 'month' ? 'month' : 'year',
    },
    documents: {
      edoId: sanitizeText(draft.documents?.edoId),
      email: sanitizeText(draft.documents?.email),
    },
    submitted: Boolean(draft.submitted),
    requestId: sanitizeText(draft.requestId),
  };
}
